import { AccordionItem, Accordion } from "@heroui/react";
import Box from "/app/src/components/generic/components/box";
import { iField, Integration } from "/app/src/models";
import {
  IconBuilder,
  IconToolTip,
} from "/app/src/components/icons/IconBuilder";
import { useCallback } from "react";
import HeroDivider from "/app/src/components/HeroUi/Divider";
import NewOrderFieldsForm from "./newOrderFieldsForm";
import { useQuery } from "@tanstack/react-query";
import { iFieldService } from "/app/src/services";
import { buildParams } from "/app/src/helpers";
import EditOrderFieldsForm from "./editOrderFieldsForm";
import { useTranslation } from "react-i18next";

/**
 * Renders the manual order fields component.
 * @param {object} props - The component props.
 * @param {Integration} props.integration - The integration data.
 * @returns {JSX.Element} The rendered component.
 */
export default function ManualOrderFields({
  integration,
}: {
  integration: Integration;
}) {
  const { t } = useTranslation();

  /**
   * Generates the title component for an accordion.
   * @param {string} title - The title text to display.
   * @returns {JSX.Element} The title component element.
   */
  const accordionTitle = (type: "order" | "line") => {
    if (type === "order") {
      return (
        <div className="flex items-center">
          <span className="text-[18px] pr-[8px]">
            {t("translation:available_order_fields")}
          </span>
          <IconToolTip
            size={24}
            content={t("translation:available_order_fields_description")}
          />
        </div>
      );
    } else {
      return (
        <div className="flex items-center">
          <span className="text-[18px] pr-[8px]">
            {t("translation:available_order_line_fields")}
          </span>
          <IconToolTip
            size={24}
            content={t("translation:available_order_line_fields_description")}
          />
        </div>
      );
    }
  };

  /**
   * Toggles the indicator icon based on open state.
   * @param {object} param0 - The object containing the open state.
   * @param {boolean} param0.isOpen - Indicates if the accordion is open.
   * @returns {JSX.Element} The indicator icon component element.
   */
  const toggleIndicatorIcon = useCallback(({ isOpen }) => {
    return isOpen ? (
      <IconBuilder icon="ChevronRight" />
    ) : (
      <IconBuilder icon="ChevronDown" />
    );
  }, []);

  const { data: orderFields } = useQuery({
    queryKey: ["orderFields", integration.id],
    queryFn: () => {
      return iFieldService.getAll(
        buildParams({ integrationId: integration.id, level: "order" }),
      );
    },
    initialData: { fields: [] },
    select: (data: { fields: iField[] }) => {
      return data.fields;
    },
  });

  const { data: orderLineFields } = useQuery({
    queryKey: ["orderLineFields", integration.id],
    queryFn: () => {
      return iFieldService.getAll(
        buildParams({ integrationId: integration.id, level: "line" }),
      );
    },
    initialData: { fields: [] },
    select: (data: { fields: iField[] }) => {
      return data.fields;
    },
  });
  return (
    <>
      <Box>
        <Accordion
          itemClasses={{
            trigger: "py-0",
          }}
          className="!px-0"
          selectionMode="single"
          showDivider
          defaultExpandedKeys={["1"]}
        >
          <AccordionItem
            textValue="order"
            key="1"
            title={accordionTitle("order")}
            indicator={toggleIndicatorIcon}
          >
            <HeroDivider className="my-3" />
            <div className="grid grid-cols-3 gap-x-6 gap-y-4">
              <div className="text-sm font-bold">
                {t("translation:power_pick_field")}
              </div>
              <div className="text-sm font-bold">
                {t("translation:order_builder_field")}
              </div>
              <div className="text-sm font-bold">
                {t("translation:required")}
              </div>
              <div />
            </div>
            {orderFields.map((field) => (
              <EditOrderFieldsForm
                type="order"
                key={field.id}
                field={field}
                integration={integration}
              />
            ))}
            <NewOrderFieldsForm
              type="order"
              fields={orderFields}
              integration={integration}
            />
          </AccordionItem>
        </Accordion>
      </Box>
      <Box>
        <Accordion
          itemClasses={{
            trigger: "py-0",
          }}
          className="!px-0"
          selectionMode="single"
          showDivider
          defaultExpandedKeys={["1"]}
        >
          <AccordionItem
            textValue="line"
            key="1"
            title={accordionTitle("line")}
            indicator={toggleIndicatorIcon}
          >
            <HeroDivider className="my-3" />
            <div className="grid grid-cols-3 gap-x-6 gap-y-4">
              <div className="text-sm font-bold">
                {t("translation:power_pick_field")}
              </div>
              <div className="text-sm font-bold">
                {t("translation:order_builder_field")}
              </div>
              <div className="text-sm font-bold">
                {t("translation:required")}
              </div>
              <div />
              <div />
            </div>
            {orderLineFields.map((field) => (
              <EditOrderFieldsForm
                type="line"
                key={field.id}
                field={field}
                integration={integration}
              />
            ))}
            <NewOrderFieldsForm
              type="line"
              fields={orderLineFields}
              integration={integration}
            />
          </AccordionItem>
        </Accordion>
      </Box>
    </>
  );
}
